// src/pages/ProtocolRulesPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  Tooltip,
  Grid,
  Checkbox, // NEW
} from '@mui/material';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material/styles';

import { Link as RouterLink } from "react-router-dom";

import DeleteIcon from '@mui/icons-material/Delete'; // NEW
import EditIcon from '@mui/icons-material/Edit';     // NEW
import ClearIcon from '@mui/icons-material/Clear';   // NEW

import { request } from '../utils/request';

const COMMON_PORTS = [
  20, 21, 22, 23, 25, 53, 67, 68, 80, 110, 123, 137, 138, 139, 143, 161, 162,
  389, 443, 445, 465, 587, 631, 993, 995, 1433, 1521, 2049, 3306, 3389, 5900,
  8080, 8443,
];

const PROTOCOLS = ['TCP', 'UDP', 'SSH', 'RDP', 'DNS'];
const ACTIONS = ['ALLOW', 'BLOCK'];

export default function ProtocolRulesPage() {
  const [rules, setRules] = useState([]);

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState({
    id: null,
    source_ip: '',
    destination_ip: '',
    source_port: '',
    destination_port: '',
    protocol: 'TCP',
    action: 'ALLOW',
    description: '',
    customSourcePort: '',
    customDestinationPort: '',
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  useEffect(() => {
    fetchRules();
  }, []);

  // three-dot menu state for per-row actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const onMenuEdit = (r) => {
    handleMenuClose();
    // reuse existing edit flow
    openEdit(r);
  };

  const onMenuDelete = (r) => {
    handleMenuClose();
    // reuse existing delete flow
    askDelete(r);
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const showSnack = (msg, severity = 'success') => {
    setSnackMsg(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };
  const handleSnackClose = (e, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const fetchRules = async () => {
    try {
      const data = await request('protocolrules', { method: 'get' });
      const arr = Array.isArray(data) ? data : [];
      const normalized = arr.map((r) => ({
        id: r._id || r.id,
        source_ip: r.source_ip || '',
        destination_ip: r.destination_ip || '',
        source_port: r.source_port || '',
        destination_port: r.destination_port || '',
        protocol: r.protocol || 'TCP',
        action: r.action || 'ALLOW',
        description: r.description || '',
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }));
      setRules(normalized);
    } catch (err) {
      console.error('Failed to fetch protocol rules', err);
      showSnack('Failed to load rules.', 'error');
      setRules([]);
    }
  };

  const openAdd = () => {
    setForm({
      id: null,
      source_ip: '',
      destination_ip: '',
      source_port: '',
      destination_port: '',
      protocol: 'TCP',
      action: 'ALLOW',
      description: '',
      customSourcePort: '',
      customDestinationPort: '',
    });
    setIsEditing(false);
    setOpen(true);
  };

  const openEdit = (r) => {
    setForm({
      id: r.id,
      source_ip: r.source_ip || '',
      destination_ip: r.destination_ip || '',
      source_port: COMMON_PORTS.includes(Number(r.source_port)) ? String(r.source_port) : 'CUSTOM',
      destination_port: COMMON_PORTS.includes(Number(r.destination_port)) ? String(r.destination_port) : 'CUSTOM',
      protocol: r.protocol || 'TCP',
      action: r.action || 'ALLOW',
      description: r.description || '',
      customSourcePort: COMMON_PORTS.includes(Number(r.source_port)) ? '' : String(r.source_port || ''),
      customDestinationPort: COMMON_PORTS.includes(Number(r.destination_port)) ? '' : String(r.destination_port || ''),
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const submitRule = async () => {
    if (!form.source_ip.trim() || !form.destination_ip.trim()) {
      showSnack('Source IP and Destination IP are required.', 'error');
      return;
    }
    const sourcePort = form.source_port === 'CUSTOM' ? Number(form.customSourcePort || 0) : Number(form.source_port || 0);
    const destPort = form.destination_port === 'CUSTOM' ? Number(form.customDestinationPort || 0) : Number(form.destination_port || 0);

    if (!sourcePort || !destPort) {
      showSnack('Both source and destination ports must be selected or provided as custom numeric ports.', 'error');
      return;
    }

    const payload = {
      source_ip: form.source_ip.trim(),
      destination_ip: form.destination_ip.trim(),
      source_port: sourcePort,
      destination_port: destPort,
      protocol: form.protocol,
      action: form.action,
      description: form.description.trim(),
    };

    try {
      if (isEditing && form.id) {
        await request(`protocolrules/${form.id}`, { method: 'put', body: payload });
        showSnack('Rule updated.', 'success');
      } else {
        await request('protocolrules', { method: 'post', body: payload });
        showSnack('Rule created.', 'success');
      }
      handleClose();
      fetchRules();
    } catch (err) {
      console.error('Failed to save rule', err);
      showSnack('Failed to save rule.', 'error');
    }
  };

  const askDelete = (r) => {
    setDeletingRule(r);
    setDeleteOpen(true);
  };
  const cancelDelete = () => {
    setDeletingRule(null);
    setDeleteOpen(false);
    setDeleting(false);
  };
  const confirmDelete = async () => {
    if (!deletingRule || !deletingRule.id) return;
    setDeleting(true);
    try {
      await request(`protocolrules/${deletingRule.id}`, { method: 'delete' });
      showSnack('Rule deleted.', 'success');
      cancelDelete();
      fetchRules();
    } catch (err) {
      console.error('Failed to delete rule', err);
      showSnack('Failed to delete rule.', 'error');
      setDeleting(false);
    }
  };

  // ---------------------------
  // NEW: selection & bulk-delete state + handlers
  // ---------------------------
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = rules.length > 0 && selectedIds.length === rules.length;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(rules.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const r = rules.find((r) => r.id === selectedIds[0]);
    if (r) {
      openEdit(r);
      // optional: clear selection after opening edit
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => request(`protocolrules/${id}`, { method: 'delete' })));
      setSelectedIds([]);
      await fetchRules();
      handleCancelBulkDelete();
      showSnack('Selected rules deleted.', 'success');
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showSnack('Failed to delete selected rules.', 'error');
      setBulkDeleting(false);
    }
  };
  // ---------------------------

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
         <RouterLink
  to="/home"
  style={{
    textDecoration: "none",
    color: "#2d9cdb",
    fontSize: "0.95rem",
  }}
  onMouseEnter={(e) => (e.currentTarget.style.color = "#0b1d51")}
  onMouseLeave={(e) => (e.currentTarget.style.color = "#2d9cdb")}
>
  Dashboard
</RouterLink>

          <Typography sx={{ fontSize: '0.95rem', color: 'black' }}>Protocol Rules</Typography>
        </Breadcrumbs>

        <Button
          size="medium"
          variant="contained"
          onClick={openAdd}
          sx={{
            backgroundColor: '#2d9cdb',
            '&:hover': { backgroundColor: '#0b1d51', color: '#fff' },
            px: 2,
            py: 0.8,
          }}
        >
          Add Rule
        </Button>
      </Stack>

      {/* --- Toolbar shown when rows selected (just above the table) --- */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography variant="body2">{selectedIds.length} selected</Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={handleEditSelected}
            disabled={selectedIds.length !== 1}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteSelected}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' }, color: '#fff', textTransform: 'none' }}
          >
            Delete
          </Button>

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper elevation={1} sx={{ mb: 3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f5f7fb" }}>
              <TableRow>
                {/* new checkbox header */}
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all rules' }}
                  />
                </TableCell>

                <TableCell>#</TableCell>
                <TableCell>Source IP</TableCell>
                <TableCell>Destination IP</TableCell>
                <TableCell>Src Port</TableCell>
                <TableCell>Dst Port</TableCell>
                <TableCell>Protocol</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">No rules found.</TableCell>
                </TableRow>
              )}
              {rules.map((r, idx) => (
                <TableRow hover key={r.id || idx} selected={selectedIds.includes(r.id)}>
                  {/* row checkbox */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(r.id)}
                      onChange={() => toggleSelect(r.id)}
                      inputProps={{ 'aria-label': `select rule ${r.id}` }}
                    />
                  </TableCell>

                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{r.source_ip}</TableCell>
                  <TableCell>{r.destination_ip}</TableCell>
                  <TableCell>{r.source_port}</TableCell>
                  <TableCell>{r.destination_port}</TableCell>
                  <TableCell>{r.protocol}</TableCell>
                  <TableCell>{r.action}</TableCell>
                  <TableCell style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={r.description || ''}><span>{r.description || '—'}</span></Tooltip>
                  </TableCell>
                  <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        aria-controls={menuRowId === r.id ? `row-menu-${r.id}` : undefined}
                        aria-haspopup="true"
                        onClick={(e) => handleMenuOpen(e, r.id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      <Menu
                        id={`row-menu-${r.id}`}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuRowId === r.id}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={() => onMenuEdit(r)}>Edit</MenuItem>
                        <MenuItem onClick={() => onMenuDelete(r)}>Delete</MenuItem>
                      </Menu>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: '#2d9cdb', color: '#fff', fontSize: '1rem', py: 1 }}>
          {isEditing ? 'Edit Protocol Rule' : 'Create Protocol Rule'}
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* IPs row - two equal columns */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ pt: 1 }}>
                <TextField
                  size="small"
                  label="Source IP"
                  name="source_ip"
                  value={form.source_ip}
                  onChange={handleFormChange}
                  fullWidth
                  placeholder="192.168.1.100"
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ pt: 1 }}>
                <TextField
                  size="small"
                  label="Destination IP"
                  name="destination_ip"
                  value={form.destination_ip}
                  onChange={handleFormChange}
                  fullWidth
                  placeholder="10.0.0.1"
                />
              </Box>
            </Grid>

            {/* Ports row - four columns (source port, custom source, dest port, custom dest) */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Source Port</InputLabel>
                <Select
                  label="Source Port"
                  name="source_port"
                  value={form.source_port || ''}
                  onChange={handleFormChange}
                >
                  {COMMON_PORTS.map((p) => (
                    <MenuItem key={`sp-${p}`} value={String(p)}>{p}</MenuItem>
                  ))}
                  <MenuItem value="CUSTOM">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                size="small"
                label="Custom Src Port"
                name="customSourcePort"
                value={form.customSourcePort}
                onChange={handleFormChange}
                fullWidth
                placeholder="e.g. 15000"
                disabled={form.source_port !== 'CUSTOM'}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Destination Port</InputLabel>
                <Select
                  label="Destination Port"
                  name="destination_port"
                  value={form.destination_port || ''}
                  onChange={handleFormChange}
                >
                  {COMMON_PORTS.map((p) => (
                    <MenuItem key={`dp-${p}`} value={String(p)}>{p}</MenuItem>
                  ))}
                  <MenuItem value="CUSTOM">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                size="small"
                label="Custom Dst Port"
                name="customDestinationPort"
                value={form.customDestinationPort}
                onChange={handleFormChange}
                fullWidth
                placeholder="e.g. 15001"
                disabled={form.destination_port !== 'CUSTOM'}
              />
            </Grid>

            {/* Protocol, Action and Description row */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Protocol</InputLabel>
                <Select name="protocol" value={form.protocol} onChange={handleFormChange} label="Protocol">
                  {PROTOCOLS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select name="action" value={form.action} onChange={handleFormChange} label="Action">
                  {ACTIONS.map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Description"
                name="description"
                value={form.description}
                onChange={handleFormChange}
                fullWidth
                placeholder="Allow access to internal web app"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button size="medium" onClick={handleClose} sx={{ color: '#2d9cdb' }}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="contained"
            onClick={submitRule}
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#0b1d51', color: '#fff' } }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Protocol Rule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this rule{' '}
            <strong>{deletingRule ? `${deletingRule.source_ip} → ${deletingRule.destination_ip}` : ''}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={cancelDelete} disabled={deleting}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' } }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={handleCancelBulkDelete}>
        <DialogTitle>Delete Selected Rules</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the following {selectedIds.length} rule(s)?</Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map((id) => {
              const it = rules.find((r) => r.id === id);
              return <Typography key={id} variant="body2">• {it ? `${it.source_ip} → ${it.destination_ip}` : id}</Typography>;
            })}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkDelete} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleConfirmBulkDelete} disabled={bulkDeleting} variant="contained" color="error">
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box >
  );
}
